import type {
  TodoNoteItemDraft,
  TodoNoteItemView,
  TodoRoutineCompleteMark,
  TodoRoutineConfig,
  TodoRoutineDayLog,
  TodoRoutineDisplay,
} from '../types/todoNote';
import { addDays, parseISODate, toISODate } from './date';
import { sortTodoItemsDrafts } from './todoNoteBucket';

export const ROUTINE_OCCURRENCE_SEP = '__';

export function defaultRoutineConfig(partial?: Partial<TodoRoutineConfig>): TodoRoutineConfig {
  return {
    frequency: partial?.frequency ?? 'weekly',
    weekdays: partial?.weekdays ?? [1, 2, 3, 4, 5],
    monthDay: partial?.monthDay ?? 1,
    startDate: partial?.startDate ?? null,
    endDate: partial?.endDate ?? null,
    display: partial?.display ?? { unit: 'week', before: 2, after: 4 },
    dayLogs: normalizeRoutineDayLogs(partial?.dayLogs),
  };
}

/** dayLogs는 수동 V(true)만 유지. false·기타는 삭제해 과거 날짜 자동 X가 다시 적용되게 함 */
export function normalizeRoutineDayLogs(
  raw: Record<string, TodoRoutineDayLog> | null | undefined
): Record<string, TodoRoutineDayLog> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, TodoRoutineDayLog> = {};
  for (const [ymd, log] of Object.entries(raw)) {
    if (!log || typeof log !== 'object') continue;
    if (log.completedOverride === true) {
      out[ymd] = { completedOverride: true };
    }
  }
  return out;
}

export function isRoutineItem(item: TodoNoteItemDraft): boolean {
  return Boolean(item.is_routine && item.routine);
}

export function makeRoutineOccurrenceId(parentId: string, ymd: string): string {
  return `${parentId}${ROUTINE_OCCURRENCE_SEP}${ymd}`;
}

export function parseRoutineOccurrenceId(
  id: string
): { parentId: string; ymd: string } | null {
  const idx = id.lastIndexOf(ROUTINE_OCCURRENCE_SEP);
  if (idx <= 0) return null;
  const parentId = id.slice(0, idx);
  const ymd = id.slice(idx + ROUTINE_OCCURRENCE_SEP.length);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return { parentId, ymd };
}

export function getRoutineDisplayRange(
  todayYmd: string,
  display: TodoRoutineDisplay
): { from: string; to: string } {
  const today = parseISODate(todayYmd);
  const before = Math.max(0, Number(display.before) || 0);
  const after = Math.max(0, Number(display.after) || 0);
  if (display.unit === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth() - before, today.getDate());
    const to = new Date(today.getFullYear(), today.getMonth() + after, today.getDate());
    return { from: toISODate(from), to: toISODate(to) };
  }
  return {
    from: toISODate(addDays(today, -before * 7)),
    to: toISODate(addDays(today, after * 7)),
  };
}

function ymdInRange(ymd: string, from: string, to: string): boolean {
  return ymd >= from && ymd <= to;
}

/** 반복 시작~마감 안에서 실제로 만들어지는 마지막 발생일. 없으면 null. */
export function lastRoutineOccurrenceYmd(rule: TodoRoutineConfig): string | null {
  const normalized = defaultRoutineConfig(rule);
  const end = (normalized.endDate ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  const start = (normalized.startDate ?? '').trim().slice(0, 10);
  const from = /^\d{4}-\d{2}-\d{2}$/.test(start) && start <= end ? start : toISODate(addDays(parseISODate(end), -400));
  const dates = iterRoutineDates(normalized, from, end);
  return dates.length ? dates[dates.length - 1] : null;
}

/** 마감일보다 오늘이 이전이어도, 더 이상 생성될 요일이 없으면 루틴은 끝난 것으로 본다. */
export function isRoutinePeriodElapsed(rule: TodoRoutineConfig, todayYmd: string): boolean {
  const last = lastRoutineOccurrenceYmd(rule);
  if (!last) return false;
  return last < todayYmd;
}

function matchesFrequency(ymd: string, rule: TodoRoutineConfig): boolean {
  const d = parseISODate(ymd);
  if (rule.startDate && ymd < rule.startDate.slice(0, 10)) return false;
  if (rule.endDate && ymd > rule.endDate.slice(0, 10)) return false;
  if (rule.frequency === 'daily') return true;
  if (rule.frequency === 'weekly') {
    const days = rule.weekdays?.length ? rule.weekdays : [1, 2, 3, 4, 5];
    return days.includes(d.getDay());
  }
  const monthDay = Math.min(31, Math.max(1, Number(rule.monthDay) || 1));
  return d.getDate() === monthDay;
}

export function iterRoutineDates(rule: TodoRoutineConfig, from: string, to: string): string[] {
  const out: string[] = [];
  let cur = parseISODate(from);
  const end = parseISODate(to);
  while (cur <= end) {
    const ymd = toISODate(cur);
    if (matchesFrequency(ymd, rule)) out.push(ymd);
    cur = addDays(cur, 1);
  }
  return out;
}

/**
 * 루틴 부모 행은 저장되지만, 발생일이 없으면 화면에는 블럭이 하나도 안 나온다.
 * 반복 시작~마감 구간에 실제 발생일이 있는지 검사한다.
 */
export function hasRoutineOccurrenceInRulePeriod(rule: TodoRoutineConfig): boolean {
  const normalized = defaultRoutineConfig(rule);
  const start = (normalized.startDate ?? '').trim().slice(0, 10);
  const end = (normalized.endDate ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return false;
  if (start > end) return false;
  if (normalized.frequency === 'weekly' && (normalized.weekdays?.length ?? 0) === 0) return false;
  return iterRoutineDates(normalized, start, end).length > 0;
}

/** 발생일이 없을 때 보여줄 안내. 유효하면 null. */
export function getRoutineOccurrenceGapMessage(rule: TodoRoutineConfig): string | null {
  const normalized = defaultRoutineConfig(rule);
  const start = (normalized.startDate ?? '').trim().slice(0, 10);
  const end = (normalized.endDate ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return '반복 시작·마감 기간을 확인해 주세요.';
  }
  if (start > end) {
    return '반복 시작일이 마감일보다 늦어요. 기간을 다시 확인해 주세요.';
  }
  if (normalized.frequency === 'weekly' && (normalized.weekdays?.length ?? 0) === 0) {
    return '반복 요일을 하나 이상 선택해 주세요.';
  }
  if (iterRoutineDates(normalized, start, end).length > 0) return null;
  if (normalized.frequency === 'weekly') {
    return '선택한 반복 기간 안에 해당하는 요일이 없어요. 기간이나 요일을 맞춰 주세요.';
  }
  if (normalized.frequency === 'monthly') {
    return '선택한 반복 기간 안에 해당하는 날짜(매월 일)가 없어요. 기간이나 날짜를 맞춰 주세요.';
  }
  return '선택한 반복 기간으로는 생성될 노트가 없어요. 기간을 다시 확인해 주세요.';
}

/**
 * 페이지 로드·목록 표시용 상태.
 * - 수동 V만 유지
 * - 그 외 과거(ymd < today)는 자동 X(비활성 완료)
 * - 오늘·미래는 미완료
 */
export function resolveRoutineOccurrenceState(
  ymd: string,
  todayYmd: string,
  log: TodoRoutineDayLog | undefined
): { completed: boolean; completeMark: TodoRoutineCompleteMark | null } {
  if (log?.completedOverride === true) {
    return { completed: true, completeMark: 'v' };
  }
  if (ymd < todayYmd) return { completed: true, completeMark: 'x' };
  return { completed: false, completeMark: null };
}

export function patchRoutineDayLog(
  parent: TodoNoteItemDraft,
  ymd: string,
  patch: Partial<TodoRoutineDayLog>
): TodoNoteItemDraft {
  const routine = defaultRoutineConfig(parent.routine ?? undefined);
  const dayLogs = { ...routine.dayLogs };
  if (patch.completedOverride === true) {
    dayLogs[ymd] = { completedOverride: true };
  } else {
    // false·미지정 → 기록 삭제 (과거는 자동 X, 오늘·미래는 빈 체크)
    delete dayLogs[ymd];
  }
  return {
    ...parent,
    is_routine: true,
    completed: false,
    routine: { ...routine, dayLogs },
  };
}

export function toggleRoutineOccurrenceComplete(
  parent: TodoNoteItemDraft,
  ymd: string,
  todayYmd: string
): TodoNoteItemDraft {
  const routine = defaultRoutineConfig(parent.routine ?? undefined);
  const log = routine.dayLogs[ymd];
  const cur = resolveRoutineOccurrenceState(ymd, todayYmd, log);
  if (cur.completeMark === 'v') {
    // V → 해제(과거면 자동 X로 복귀)
    return patchRoutineDayLog(parent, ymd, { completedOverride: false });
  }
  // X 또는 미완료 → 수동 V
  return patchRoutineDayLog(parent, ymd, { completedOverride: true });
}

export function expandTodoItemsForView(
  items: TodoNoteItemDraft[],
  todayYmd: string
): TodoNoteItemView[] {
  const out: TodoNoteItemView[] = [];
  for (const it of items) {
    if (!isRoutineItem(it) || !it.routine) {
      out.push({
        ...it,
        is_routine: false,
        completeMark: it.completed ? 'v' : null,
      });
      continue;
    }
    const rule = defaultRoutineConfig(it.routine);
    const elapsed = isRoutinePeriodElapsed(rule, todayYmd);
    const { from, to } = getRoutineDisplayRange(todayYmd, rule.display);
    const dates = iterRoutineDates(rule, from, to);
    for (const ymd of dates) {
      if (!ymdInRange(ymd, from, to)) continue;
      const log = rule.dayLogs[ymd];
      const { completed, completeMark } = elapsed
        ? {
            completed: true,
            completeMark: (log?.completedOverride === true ? 'v' : 'x') as TodoRoutineCompleteMark,
          }
        : resolveRoutineOccurrenceState(ymd, todayYmd, log);
      out.push({
        ...it,
        id: makeRoutineOccurrenceId(it.id, ymd),
        due_kind: 'day',
        due_date: ymd,
        completed,
        completeMark,
        body: it.body,
        misc: it.misc,
        routineParentId: it.id,
        routineOccurrenceDate: ymd,
        is_routine: true,
        routine: it.routine,
      });
    }
  }
  return sortTodoItemsDrafts(out) as TodoNoteItemView[];
}

export function findRoutineParent(
  items: TodoNoteItemDraft[],
  viewItem: TodoNoteItemView | TodoNoteItemDraft
): TodoNoteItemDraft | null {
  const parentId =
    'routineParentId' in viewItem && viewItem.routineParentId
      ? viewItem.routineParentId
      : parseRoutineOccurrenceId(viewItem.id)?.parentId;
  if (!parentId) return null;
  return items.find((x) => x.id === parentId) ?? null;
}
